import { atom, map } from 'nanostores';
import { parse, serialize } from 'cookie';
import type { FrontendData } from '~/types/figma-simplified-api';
import { uploadAssetToGCS } from '~/lib/utils/assetClientUpload';
import { v4 as uuidv4 } from 'uuid';
import type { AssetSource, AssetType } from '~/types/asset';
import { ALLOWED_ASSET_IMAGE_EXTENSIONS, isDev, MAX_ATTACHED_IMAGES } from '~/utils/constants';
import { toast } from 'react-toastify';
import axios from 'axios';
import * as Sentry from '@sentry/remix';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Represents an attached image with its source type
 */
export interface AttachedImage {
  id: string;
  base64: string;
  gcsURL?: string;
  source: 'figma' | 'upload' | 'paste';
  addedAt: number;
  isAttaching: boolean;
}

/**
 * Figma OAuth token state
 */
export interface FigmaTokenState {
  accessToken: string | null;
  isValid: boolean;
  validatedAt: number | null;
  userEmail: string | null;
  userImageUrl: string | null;
}

/**
 * Extracted Figma design data (extends FrontendData)
 */
export interface FigmaDesignState {
  design: FrontendData | null;
  figmaUrl: string;
  extractedAt: number | null;
  hasError: boolean;
  errorType: 'size' | 'dimension' | 'jsonSize' | null;
}

/**
 * UI state for the Figma modal and related components
 */
export interface FigmaUIState {
  isModalOpen: boolean;
  isExtracting: boolean;
  isExtractionDone: boolean;
  isValidatingToken: boolean;
  showResetState: boolean;
}

/**
 * Connection status for Figma integration
 */
export interface FigmaConnectionStatus {
  isConnected: boolean;
  isDisconnected: boolean;
}

// ============================================================================
// STORES
// ============================================================================

/**
 * Store for Figma OAuth token state
 * Token itself stored in cookies, but validation state in nanostore
 */
export const figmaTokenStore = map<FigmaTokenState>({
  accessToken: null,
  isValid: false,
  validatedAt: null,
  userEmail: null,
  userImageUrl: null,
});

/**
 * Store for extracted Figma design data
 */
export const figmaDesignStore = map<FigmaDesignState>({
  design: null,
  figmaUrl: '',
  extractedAt: null,
  hasError: false,
  errorType: null,
});

/**
 * Store for Figma modal UI state
 */
export const figmaUIStore = map<FigmaUIState>({
  isModalOpen: false,
  isExtracting: false,
  isExtractionDone: false,
  isValidatingToken: false,
  showResetState: false,
});

/**
 * Store for attached images (both Figma and regular uploads)
 */
export const attachedImagesStore = atom<AttachedImage[]>([]);

/**
 * Store for Figma connection status
 */
export const figmaConnectionStatusStore = map<FigmaConnectionStatus>({
  isConnected: false,
  isDisconnected: false,
});

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const MIME_TO_EXTENSION: Record<string, string> = {
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
};

function getExtensionFromDataUrl(dataUrl: string): string | null {
  if (!dataUrl.startsWith('data:')) {
    return null;
  }

  const match = dataUrl.match(/^data:([^;]+);/i);

  if (!match) {
    return null;
  }

  return MIME_TO_EXTENSION[match[1].toLowerCase()] ?? null;
}

function isAllowedImageDataUrl(dataUrl: string): boolean {
  const extension = getExtensionFromDataUrl(dataUrl);
  return (
    !!extension && ALLOWED_ASSET_IMAGE_EXTENSIONS.includes(extension as (typeof ALLOWED_ASSET_IMAGE_EXTENSIONS)[number])
  );
}

// ----- Token Management -----

export function initializeFigmaToken(): void {
  if (typeof document === 'undefined') {
    return;
  }

  const cookies = parse(document.cookie);
  const token = cookies.dualite_figma_token;

  if (token) {
    figmaTokenStore.setKey('accessToken', token);
  } else {
    // Check localStorage for migration
    const localToken = localStorage.getItem('dualite_figma_token');

    if (localToken) {
      setFigmaToken(localToken);

      // Remove from localStorage
      localStorage.removeItem('dualite_figma_token');

      const localKeys = localStorage.getItem('dualite_api_keys');

      if (localKeys) {
        document.cookie = serialize('dualite_api_keys', localKeys, {
          path: '/',
          secure: window.location.protocol === 'https:',
          sameSite: 'lax',
          maxAge: 300,
        });
        localStorage.removeItem('dualite_api_keys');
      }
    }
  }
}

export function setFigmaToken(token: string, userData?: { email?: string; img_url?: string }): void {
  // Store token in cookie (30 days)
  document.cookie = serialize('dualite_figma_token', token, {
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'lax',
    maxAge: 30 * 24 * 60 * 60, // 30 days
  });

  figmaTokenStore.set({
    accessToken: token,
    isValid: true,
    validatedAt: Date.now(),
    userEmail: userData?.email ?? null,
    userImageUrl: userData?.img_url ?? null,
  });

  updateFigmaConnectionStatus();
}

export async function clearFigmaToken(): Promise<void> {
  try {
    axios.delete('/api/user-integration', {
      data: {
        provider: 'figma',
      },
    });

    // Remove cookies
    document.cookie = serialize('dualite_figma_token', '', {
      path: '/',
      maxAge: -1,
    });

    document.cookie = serialize('dualite_api_keys', '', {
      path: '/',
      maxAge: -1,
    });

    figmaTokenStore.set({
      accessToken: null,
      isValid: false,
      validatedAt: null,
      userEmail: null,
      userImageUrl: null,
    });

    updateFigmaConnectionStatus();
  } catch (error) {
    console.error('Error deleting user integration from SupabaseOAuthModal:', error);
    Sentry.captureException(error);
    toast.error('Failed to disconnect Figma. Please try again or refresh the page.');
  }
}

export function setFigmaTokenValidation(isValid: boolean): void {
  figmaTokenStore.setKey('isValid', isValid);
  figmaTokenStore.setKey('validatedAt', isValid ? Date.now() : null);

  updateFigmaConnectionStatus();
}

// ----- Design Management -----

export function setFigmaDesign(design: FrontendData, figmaUrl: string): void {
  figmaDesignStore.set({
    design,
    figmaUrl,
    extractedAt: Date.now(),
    hasError: false,
    errorType: null,
  });

  // console.log('>>>[figma] setFigmaDesign called: ', design);

  // Also add the Figma image to attached images store
  if (design.image) {
    // console.log('>>>[figma] setFigmaDesign adding image to attached images store: ', design.image);
    addAttachedImage(design.image, 'figma');
  }
}

export function setFigmaDesignError(errorType: 'size' | 'dimension' | 'jsonSize', image?: string): void {
  figmaDesignStore.setKey('hasError', true);
  figmaDesignStore.setKey('errorType', errorType);

  // Still add the image for preview even if there's an error
  if (image) {
    addAttachedImage(image, 'figma');
  }
}

export function clearFigmaDesign(): void {
  const currentDesign = figmaDesignStore.get();

  // Remove Figma image from attached images
  if (currentDesign.design?.image) {
    const currentImages = attachedImagesStore.get();
    const filteredImages = currentImages.filter((img) => img.source !== 'figma');
    attachedImagesStore.set(filteredImages);
  }

  figmaDesignStore.set({
    design: null,
    figmaUrl: '',
    extractedAt: null,
    hasError: false,
    errorType: null,
  });
}

export function setFigmaUrl(url: string): void {
  figmaDesignStore.setKey('figmaUrl', url);
}

// ----- UI State Management -----

export function openFigmaModal(resetState: boolean = false): void {
  figmaUIStore.set({
    isModalOpen: true,
    isExtracting: false,
    isExtractionDone: figmaDesignStore.get().design !== null,
    isValidatingToken: false,
    showResetState: resetState,
  });
}

export function closeFigmaModal(): void {
  figmaUIStore.setKey('isModalOpen', false);
}

export function setFigmaExtracting(isExtracting: boolean): void {
  figmaUIStore.setKey('isExtracting', isExtracting);
}

export function setFigmaExtractionDone(isDone: boolean): void {
  figmaUIStore.setKey('isExtractionDone', isDone);
}

export function setFigmaValidatingToken(isValidating: boolean): void {
  figmaUIStore.setKey('isValidatingToken', isValidating);
}

// ----- Attached Images Management and Compression -----

/**
 * Converts an image URL to a Base64 Data URL string.
 * @param url The URL of the image to fetch.
 * @returns A Promise that resolves to the Base64 string (e.g., "data:image/webp;base64,...")
 */
export const compressImageUrlToBase64 = async (url: string, signal?: AbortSignal): Promise<string> => {
  try {
    const proxyUrl = isDev
      ? `https://proxy-worker.old-credit-270e.workers.dev/image?url=${encodeURIComponent(url)}`
      : url;
    const initialUrl = isDev ? proxyUrl : url;

    const fetchImage = async (targetUrl: string): Promise<Response> => {
      const response = await fetch(targetUrl, { signal });

      if (!response.ok) {
        throw new Error(`Failed to fetch image. Status: ${response.status}`);
      }

      return response;
    };

    let response: Response;

    try {
      response = await fetchImage(initialUrl);
    } catch (error) {
      if (isDev || initialUrl === proxyUrl) {
        throw error;
      }

      response = await fetchImage(proxyUrl);
    }

    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        const base64String = reader.result as string;
        resolve(base64String);
      };

      reader.onerror = () => {
        reject(new Error('Failed to convert blob to base64'));
      };

      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error in imageUrlToBase64:', error);
    throw error;
  }
};

export const compressImage = async (
  image: string,
  source: AssetSource,
  signal?: AbortSignal,
): Promise<{ base64: string; gcsURL: string }> => {
  const jobId = uuidv4();

  try {
    const result = await uploadAssetToGCS(
      image,
      {
        assetType: 'image',
        source,
        jobIdParam: jobId,
      },
      signal,
    );

    console.log('[handleAssetUpload] Upload result:', {
      status: result.status,
      jobIdParam: result.jobIdParam,
      finalUrl: result.finalUrl,
      error: result.error,
    });

    if (result.status === 'completed') {
      console.log(`[handleAssetUpload] Upload completed successfully for file`, {
        jobIdParam: result.jobIdParam,
        finalUrl: result.finalUrl,
      });

      if (!result.finalUrl) {
        throw new Error('No finalUrl returned from upload');
      }

      const base64Img = await compressImageUrlToBase64(result.finalUrl, signal);
      console.log('[handleAssetUpload] base64image: ', base64Img);

      return {
        base64: base64Img,
        gcsURL: result.finalUrl,
      };
    } else {
      if (result.error === 'Extraction cancelled') {
        throw new Error(result.error || 'Extraction cancelled');
      }

      console.error('[handleAssetUpload] Upload failed:', {
        status: result.status,
        error: result.error,
        jobIdParam: result.jobIdParam,
      });
      throw new Error(result.error);
    }
  } catch (error) {
    throw error;
  }
};

export async function addAttachedImage(base64: string, source: 'figma' | 'upload' | 'paste'): Promise<void> {
  let currentImages = attachedImagesStore.get();

  if (base64.startsWith('data:') && !isAllowedImageDataUrl(base64)) {
    toast.error(`Unsupported image type. Allowed: ${ALLOWED_ASSET_IMAGE_EXTENSIONS.join(', ')}`);
    return;
  }

  // Check if this exact image already exists
  const exists = currentImages.some((img) => img.base64 === base64);

  // console.log('>>>[figma] addAttachedImage exists: ', exists);

  if (!exists) {
    // console.log('>>>[figma] addAttachedImage image does not exist: ', base64);

    const newImage: AttachedImage = {
      id: `${source}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      base64,
      source,
      addedAt: Date.now(),
      isAttaching: true,
    };

    if (source === 'figma') {
      // console.log('>>>[figma] addAttachedImage setting image to the store: ', newImage);

      attachedImagesStore.set([{ ...newImage, isAttaching: false }]);
      return;
    }

    // set the raw image to the store
    attachedImagesStore.set([...currentImages, newImage]);

    console.log('>>>[addAttachedImage] currentImages before compression: ', newImage);

    //set the compressed image to the store if source is upload or paste
    if (source === 'upload' || source === 'paste') {
      console.log('>>>[addAttachedImage] compressing image');

      try {
        const { base64: compressedBase64, gcsURL } = await compressImage(base64, 'prompt');
        newImage.base64 = compressedBase64;
        newImage.gcsURL = gcsURL;
      } catch (error) {
        console.error('Error in compressImage:', error);
        toast.error('Error in uploading the image. Please try again.');
        removeAttachedImage(newImage.id);
        return;
      }
    }

    currentImages = attachedImagesStore.get();

    console.log('>>>[addAttachedImage] setting compressed image to the store: ', newImage);
    console.log('>>>[addAttachedImage] currentImages: ', currentImages);

    attachedImagesStore.set(
      currentImages.map((img) => {
        if (img.id === newImage.id) {
          return { ...newImage, isAttaching: false };
        }

        return img;
      }),
    );
  }
}

export async function addAttachedImages(images: string[], source: 'upload' | 'paste' | 'figma'): Promise<void> {
  const validImages = images.filter((img) => !img.startsWith('data:') || isAllowedImageDataUrl(img));
  const invalidCount = images.length - validImages.length;

  if (invalidCount > 0) {
    toast.error(`Unsupported image type(s). Allowed: ${ALLOWED_ASSET_IMAGE_EXTENSIONS.join(', ')}`);
  }

  await Promise.allSettled(validImages.map((img) => addAttachedImage(img, source)));
}

export function restoreAttachedImages(images: AttachedImage[]): void {
  const currentImages = attachedImagesStore.get();

  // Filter out any duplicates
  const newImages = images.filter(
    (img) => !currentImages.some((curr) => curr.id === img.id || curr.base64 === img.base64),
  );

  if (newImages.length > 0) {
    // Ensure they are marked as not attaching
    const readyImages = newImages.map((img) => ({ ...img, isAttaching: false }));
    attachedImagesStore.set([...currentImages, ...readyImages]);
  }
}

export function removeAttachedImage(imageId: string): void {
  const currentImages = attachedImagesStore.get();
  const imageToRemove = currentImages.find((img) => img.id === imageId);

  // If removing a Figma image, also clear the design
  if (imageToRemove?.source === 'figma') {
    clearFigmaDesign();
    return; // clearFigmaDesign already removes the image
  }

  attachedImagesStore.set(currentImages.filter((img) => img.id !== imageId));
}

export function removeAttachedImageByIndex(index: number): void {
  const currentImages = attachedImagesStore.get();
  const imageToRemove = currentImages[index];

  if (imageToRemove) {
    removeAttachedImage(imageToRemove.id);
  }
}

export function clearAllAttachedImages(): void {
  console.log('>>>[HomeView] clearAllAttachedImages');
  attachedImagesStore.set([]);
}

export function clearFigmaDesignAndImages(): void {
  clearFigmaDesign();
  clearAllAttachedImages();
}

export function getNonFigmaImages(): AttachedImage[] {
  return attachedImagesStore.get().filter((img) => img.source !== 'figma');
}

export function getFigmaImage(): AttachedImage | undefined {
  return attachedImagesStore.get().find((img) => img.source === 'figma');
}

export function getAttachedImageCount(): number {
  return attachedImagesStore.get().length;
}

export function canAttachMoreImages(maxImages: number = MAX_ATTACHED_IMAGES): boolean {
  const nonFigmaCount = getNonFigmaImages().length;
  const hasFigma = getFigmaImage() !== undefined;

  // If Figma is attached, no more images allowed
  if (hasFigma) {
    return false;
  }

  return nonFigmaCount < maxImages;
}

// ----- Connection Status -----

export function updateFigmaConnectionStatus(): void {
  const tokenState = figmaTokenStore.get();
  const designState = figmaDesignStore.get();

  const hasToken = !!tokenState.accessToken;
  const hasValidToken = tokenState.isValid;
  const hasDesign = !!designState.design;

  figmaConnectionStatusStore.set({
    isConnected: hasToken && hasValidToken,
    isDisconnected: !hasToken && hasDesign, // Had design but lost token
  });
}

// ----- Full Reset -----

export async function resetAllFigmaState(): Promise<void> {
  await clearFigmaToken();
  clearFigmaDesign();

  // clearAllAttachedImages();

  figmaUIStore.set({
    isModalOpen: false,
    isExtracting: false,
    isExtractionDone: false,
    isValidatingToken: false,
    showResetState: false,
  });
}
